import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import StatusChip from '../../Shared/StatusChip';
import { IssueSeverity } from '../../../shared/constants';
import {
  faBarsFilter,
  faCircleExclamation,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import DashboardTableLegend from './DashboardTableLegend';
import CustomerCareDTable from '../../Shared/CustomerCareDTable';
import OverviewPopover from './OverviewPopover';
import NoDataCard from './NoDataCard';
import LoadingSpinner from '../../Shared/LoadingSpinner';
import { fetchHubOwners } from '../../../store/hubOwner/actionCreators';
import { parseDateToLocal } from '../../../shared/utils';
import { HubOwner } from '../../../store/hubOwner/types';
import { ReactComponent as MailIcon } from '../../../images/icons/mail.svg';
import { ReactComponent as CallIcon } from '../../../images/icons/call.svg';
import {
  customError,
  dimGrey,
  lightBackground1,
  warningBg,
  warningYellow,
} from '../../../mainTheme';

interface IssuesDetailPanelProps {
  selectedTab: number;
  isFacilityAdmin: boolean;
}

interface ActionCellProps {
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
}

const ActionCell = ({
  contactName,
  contactPhone,
  contactEmail,
}: ActionCellProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleEmailClick = () => {
    if (contactEmail) {
      window.location.href = `mailto:${contactEmail}`;
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      borderRadius="8px"
      border="1px solid var(--Outline-O5---Separator, #D4D4D9)"
    >
      <IconButton
        size="small"
        onClick={handleOpenModal}
        sx={{
          marginX: '4px',
          width: 32,
          height: 32,
          p: 0,
        }}
      >
        <CallIcon style={{ width: 20, height: 20 }} />
      </IconButton>

      <Box
        width="1px"
        height="32px"
        bgcolor="var(--Outline-O5---Separator, #D4D4D9)"
      />
      <IconButton
        size="small"
        onClick={handleEmailClick}
        sx={{
          marginX: '4px',
          width: 32,
          height: 32,
          borderRadius: '12px',
          p: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <MailIcon style={{ width: 20, height: 20 }} />
      </IconButton>
      {isModalOpen && (
        <OverviewPopover
          onClose={handleCloseModal}
          contactName={contactName}
          contactPhone={contactPhone}
        />
      )}
    </Box>
  );
};

const IssuesDetailPanel: React.FC<IssuesDetailPanelProps> = ({
  selectedTab,
  isFacilityAdmin,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { hubOwners, isLoading } = useSelector(
    (state: RootState) => state.hubOwner,
  );

  useEffect(() => {
    if (!hubOwners?.length) {
      dispatch(fetchHubOwners());
    }
  }, [dispatch, hubOwners?.length]);

  const onViewAll = () => {
    navigate(`/v2/issues`);
  };

  const columns = [
    {
      field: 'patient',
      headerName: 'Patient',
      flex: 1,
    },
    ...(selectedTab !== 2
      ? [
          {
            field: 'facility_name',
            headerName: isFacilityAdmin ? 'Facility location' : 'Facility',
            flex: 1,
          },
        ]
      : []),
    {
      field: 'active_issue',
      headerName: 'Issue',
      flex: 1,
      sortComparator: (v1: any, v2: any) => {
        return v1.text.localeCompare(v2.text);
      },
      renderCell: (params: any) => {
        const { icon, color, text, conditionalValue, allIssues } = params.value;
        const hasMultiple = Array.isArray(allIssues) && allIssues.length > 1;
        if (hasMultiple) {
          return (
            <Box
              display="flex"
              alignItems="center"
              sx={{
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <Tooltip
                title={
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: lightBackground1,
                      border: '1px solid #D4D4D9',
                      borderRadius: '8px',
                      boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                      maxWidth: {
                        xs: '18.75rem',
                        sm: '37.5rem',
                        md: '53.125rem',
                        lg: '62.5rem',
                        xl: '78.125rem',
                      },
                      color: dimGrey,
                      wordWrap: 'break-word',
                      whiteSpace: 'normal',
                    }}
                  >
                    {allIssues.map((issue: any, idx: number) => (
                      <Typography
                        key={idx}
                        sx={{
                          fontWeight: 500,
                          fontSize: '16px',
                          lineHeight: '24px',
                          letterSpacing: '0.2px',
                        }}
                      >
                        {issue.title}
                      </Typography>
                    ))}
                  </Box>
                }
                placement="bottom-start"
                componentsProps={{
                  tooltip: {
                    sx: { bgcolor: 'transparent', p: 0, maxWidth: 'none' },
                  },
                }}
              >
                <Box
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <StatusChip
                    icon={icon}
                    iconColor={color}
                    labelText={text}
                    conditionalValue={conditionalValue}
                    backgroundColor={warningBg}
                  />
                </Box>
              </Tooltip>
            </Box>
          );
        }
        return (
          <Box display="flex" alignItems="center">
            <StatusChip
              icon={icon}
              iconColor={color}
              labelText={text}
              conditionalValue={conditionalValue}
              backgroundColor={warningBg}
            />
          </Box>
        );
      },
    },
    { field: 'hub_serial_number', headerName: 'Hub serial number', flex: 1 },
    { field: 'last_seen_online', headerName: 'Last online', flex: 1 },
    {
      field: 'action',
      headerName: 'Actions',
      renderCell: (params: any) => (
        <ActionCell
          contactName={params.row.contact_name}
          contactPhone={params.row.contact_phone}
          contactEmail={params.row.contact_email}
        />
      ),
    },
  ];

  // Helper to normalize severity value
  const normalizeSeverity = (severity: string) => {
    if (severity === 'HIGH' || severity === IssueSeverity.HIGH)
      return IssueSeverity.HIGH;
    if (severity === 'LOW' || severity === IssueSeverity.LOW)
      return IssueSeverity.LOW;
    if (severity === '1') return IssueSeverity.HIGH;
    if (severity === '3') return IssueSeverity.LOW;
    return severity; // fallback
  };

  const getIssueStatusConfig = (activeIssues: any[]) => {
    if (activeIssues.length === 1) {
      const issue = activeIssues[0];
      const normalizedSeverity = normalizeSeverity(issue.severity);
      return {
        icon:
          normalizedSeverity === IssueSeverity.HIGH
            ? faTriangleExclamation
            : faCircleExclamation,
        color:
          normalizedSeverity === IssueSeverity.HIGH
            ? customError
            : warningYellow,
        text: issue.title,
        conditionalValue: normalizedSeverity === IssueSeverity.LOW,
      };
    }

    const hasHighPriority = activeIssues.some(
      (issue) => normalizeSeverity(issue.severity) === IssueSeverity.HIGH,
    );
    return {
      icon: hasHighPriority ? faTriangleExclamation : faCircleExclamation,
      color: hasHighPriority ? customError : warningYellow,
      text: `${activeIssues.length} issues`,
      conditionalValue: !hasHighPriority,
    };
  };

  const rows: any[] = useMemo(() => {
    let filteredHubOwners = hubOwners || [];

    filteredHubOwners = filteredHubOwners.filter((owner: any) => {
      return owner?.active_issue?.length > 0;
    });

    if (selectedTab === 1) {
      filteredHubOwners = filteredHubOwners.filter(
        (owner: any) => owner.facility_id != null,
      );
    } else if (selectedTab === 2) {
      filteredHubOwners = filteredHubOwners.filter(
        (owner: any) => owner.facility_id == null,
      );
    }

    return (
      filteredHubOwners?.map((owner: HubOwner) => {
        const hasPatient = owner?.first_name || owner?.last_name;
        const activeIssueStatus = getIssueStatusConfig(owner.active_issue);

        // Adapt action values based on Role
        const contactName = isFacilityAdmin
          ? `${owner?.first_name ?? ''} ${owner?.last_name ?? ''}`.trim()
          : owner.contact_name || null;
        const contactPhone = isFacilityAdmin
          ? owner.phone || null
          : owner.contact_phone || null;
        const contactEmail = isFacilityAdmin
          ? owner.email || null
          : owner.contact_email || null;

        return {
          id: owner.customer_id,
          patient: hasPatient
            ? `${owner?.first_name} ${owner?.last_name}`
            : 'N/A',
          facility_name: owner.facility_name || 'N/A',
          active_issue: {
            icon: activeIssueStatus.icon,
            color: activeIssueStatus.color,
            text: activeIssueStatus.text,
            conditionalValue: activeIssueStatus.conditionalValue,
            allIssues: owner.active_issue || [],
          },
          hub_serial_number: owner?.serial_number || 'N/A',
          last_seen_online: owner.last_seen_online
            ? parseDateToLocal(owner.last_seen_online, 'yyyy-MM-dd HH:mm:ss')
            : 'N/A',
          action: faBarsFilter,
          contact_name: contactName,
          contact_phone: contactPhone,
          contact_email: contactEmail,
        };
      }) ?? []
    );
  }, [hubOwners, selectedTab]);

  const tableLegendData = [
    {
      name: 'High priority',
      color: customError,
    },
    {
      name: 'Low priority',
      color: warningYellow,
    },
  ];

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {rows.length ? (
        <Box p={2}>
          <DashboardTableLegend tableLegendData={tableLegendData} />
          <CustomerCareDTable
            rows={rows}
            columns={columns}
            loading={isLoading}
            pageSize={5}
            onViewAll={onViewAll}
            defaultSortField="active_issue"
            defaultSortDirection="asc"
          />
        </Box>
      ) : (
        <NoDataCard />
      )}
    </>
  );
};

export default IssuesDetailPanel;
